import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useNavigate, Link as RouterLink, useLocation, useSearchParams } from "react-router-dom";
import { UserContext } from "../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../platform/redux/store";
import { MarketplaceListingType } from "../marketplaceStorage";
import { useCreateMarketplaceListing } from "../hooks/useMarketplaceApi";
import { useMutation } from "@apollo/client";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { GENERATE_UPLOAD_URL } from "../../../../platform/graphql/mutations/image";
import { uploadToGCS } from "../../../../platform/util/upload/uploadToGCS";
import PayInvoiceDialog from "../../../referralCareDirectory/careReferral/components/payInvoiceDialog";
import { getEffectivePlan } from "../../../../platform/util/utils";

const GIFT_CATEGORY_OPTIONS = [
  "Mobility & Safety",
  "Comfort & Bedding",
  "Memory Care Activities",
  "Daily Living Aids",
  "Wellness & Therapy",
  "Adaptive Clothing",
  "Entertainment & Games",
  "Personal Care",
  "Other",
] as const;

const AGE_GROUP_OPTIONS = [
  "All",
  "55+",
  "60+",
  "65+",
  "70+",
  "75+",
  "80+",
  "85+",
  "90+",
] as const;

const AFH_LISTING_POST_PRICE_USD = 49;
const GIFT_LISTING_FEE_MIN_USD = 5;

const getGiftListingPostFeeUsd = (itemPrice: number) => {
  if (itemPrice <= 50) return 5;
  if (itemPrice <= 200) return 9;
  if (itemPrice <= 500) return 19;
  return 29;
};

const getListingPostFeeUsd = (type: MarketplaceListingType, itemPrice: number) => {
  if (type === "AFH_SALE") return AFH_LISTING_POST_PRICE_USD;
  return getGiftListingPostFeeUsd(itemPrice);
};

const fileNameFromUrl = (url?: string | null) => {
  if (!url) return "";
  try {
    const last = decodeURIComponent(url.split("/").pop() || "");
    return (last.split("?")[0] || "").trim();
  } catch {
    return "";
  }
};

export default function MarketplaceCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const selectedBusiness = useSelectedBusiness();
  const { providerUser, user } = React.useContext(UserContext) as any;
  const loggedInUser = providerUser || user || null;
  const standardPlanRequired = 2;
  const hasStandardPlan =
    Number(getEffectivePlan(loggedInUser) || 0) >= standardPlanRequired;

  const queryType = (params.get("type") || "").toUpperCase();
  const loginHref = `/login?returnTo=${encodeURIComponent(
    `${location.pathname}${location.search}`
  )}`;
  const defaultType: MarketplaceListingType =
    queryType === "AFH_SALE" ? "AFH_SALE" : "ELDER_GIFT";

  const [type, setType] = React.useState<MarketplaceListingType>(defaultType);
  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [price, setPrice] = React.useState("");
  const [city, setCity] = React.useState("");
  const [state, setState] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState(String(loggedInUser?.email || ""));
  const [contactPhone, setContactPhone] = React.useState("");
  const [beds, setBeds] = React.useState("");
  const [capacity, setCapacity] = React.useState("");
  const [occupancy, setOccupancy] = React.useState("");
  const [giftCategory, setGiftCategory] = React.useState("");
  const [giftCategoryOther, setGiftCategoryOther] = React.useState("");
  const [ageGroup, setAgeGroup] = React.useState("");
  const [giftImageUrls, setGiftImageUrls] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [payOpen, setPayOpen] = React.useState(false);
  const [invoice, setInvoice] = React.useState<{ id: string; amount: number } | null>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const { create, loading: saving } = useCreateMarketplaceListing();
  const [mutGenerateUploadUrl] = useMutation(GENERATE_UPLOAD_URL);
  const imageInputRef = React.useRef<HTMLInputElement>(null);
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  const businessId = selectedBusiness?.businessBasic?.id;
  const businessName = selectedBusiness?.businessBasic?.name || "";
  const businessStaffs = selectedBusiness?.businessStaffs || [];
  const myStaff = businessStaffs.find(
    (s: any) => Number(s?.id) === Number(loggedInUser?.staffId)
  );
  const isBusinessAdmin =
    String(myStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";

  const isAfhSale = type === "AFH_SALE";
  const numericPrice = Number(price || 0);
  const listingPostFeeUsd = getListingPostFeeUsd(type, numericPrice);
  const missingBusinessForAfh = isAfhSale && !businessId;
  const isGiftCategoryOther = giftCategory === "Other";
  const maxCapacityOption = Math.max(16, Number(beds || 0), Number(capacity || 0));
  const capacityOptions = React.useMemo(
    () => Array.from({ length: maxCapacityOption }, (_, i) => String(i + 1)),
    [maxCapacityOption]
  );
  const occupancyOptions = React.useMemo(() => {
    const max = Math.max(0, Number(capacity || beds || 0));
    return Array.from({ length: max + 1 }, (_, i) => String(i));
  }, [capacity, beds]);

  const publishListing = async () => {
    const normalizedGiftImages = giftImageUrls
      .map((u) => String(u || "").trim())
      .filter(Boolean);
    await create({
      type,
      title: title.trim(),
      description: description.trim(),
      price: Number(price),
      city: city.trim(),
      state: state.trim().toUpperCase(),
      contactEmail: contactEmail.trim(),
      contactPhone: contactPhone.trim() || undefined,
      ownerBusinessId: isAfhSale ? String(businessId) : undefined,
      ownerBusinessName: isAfhSale ? businessName : undefined,
      beds: isAfhSale && beds ? Number(beds) : undefined,
      capacity: isAfhSale && capacity ? Number(capacity) : undefined,
      occupancy: isAfhSale && occupancy ? Number(occupancy) : undefined,
      giftCategory: !isAfhSale
        ? (isGiftCategoryOther ? giftCategoryOther.trim() : giftCategory.trim()) || undefined
        : undefined,
      ageGroup: !isAfhSale ? ageGroup.trim() || undefined : undefined,
      giftImageUrl: !isAfhSale ? normalizedGiftImages[0] || undefined : undefined,
      imageUrls: !isAfhSale ? normalizedGiftImages : undefined,
    });
    navigate("/care/marketplace");
  };

  const save = async () => {
    setError(null);
    if (!loggedInUser?.userId && !loggedInUser?.email) {
      setError("Please sign in before posting a listing.");
      return;
    }
    if (!title.trim()) return setError("Title is required.");
    if (!description.trim()) return setError("Description is required.");
    if (!numericPrice) return setError("Price is required.");
    if (!city.trim() || !state.trim()) return setError("City and state are required.");
    if (!contactEmail.trim()) return setError("Contact email is required.");
    if (missingBusinessForAfh) {
      return setError("Select your AFH business first, then post AFH sale listing.");
    }
    if (isAfhSale && !isBusinessAdmin) {
      return setError("Only business admins can post AFH sale listings.");
    }
    if (!isAfhSale && !giftCategory.trim()) {
      return setError("Select a gift category.");
    }
    if (!isAfhSale && isGiftCategoryOther && !giftCategoryOther.trim()) {
      return setError("Enter your custom gift category.");
    }

    if (!hasStandardPlan) {
      setInvoice({
        id: `LISTINGPOST-${type}-${Date.now()}`,
        amount: listingPostFeeUsd,
      });
      setPayOpen(true);
      return;
    }

    try {
      await publishListing();
    } catch (e: any) {
      setError(e?.message || "Failed to publish listing.");
    }
  };

  const removeGiftImageUrl = (index: number) => {
    setGiftImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const onPickGiftImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputEl = e.currentTarget;
    const file = inputEl.files?.[0] || null;
    if (!file) return;
    try {
      setUploadingImage(true);
      setError(null);
      const url = await uploadToGCS(file, mutGenerateUploadUrl);
      const clean = String(url || "").trim();
      if (clean) {
        setGiftImageUrls((prev) => [...prev, clean]);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to upload image.");
    } finally {
      setUploadingImage(false);
      inputEl.value = "";
    }
  };

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", px: { xs: 1.5, sm: 2 }, py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>
          Post Marketplace Listing
        </Typography>
        <Button component={RouterLink} to="/care/marketplace" variant="outlined">
          Back to Marketplace
        </Button>
      </Stack>

      {!loggedInUser?.email && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          action={
            <Button component={RouterLink} to={loginHref} size="small">
              Sign in
            </Button>
          }
        >
          You need to sign in to post listings.
        </Alert>
      )}
      {!hasStandardPlan && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {isAfhSale
            ? `Listing publish is free on Standard+ plans. You can also publish one AFH listing for $${AFH_LISTING_POST_PRICE_USD}.`
            : `Listing publish is free on Standard+ plans. Gift/product listing fees start at $${GIFT_LISTING_FEE_MIN_USD} and are based on item price. Current fee: $${listingPostFeeUsd}.`}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={1.25}>
            {!!error && <Alert severity="error">{error}</Alert>}

            <TextField
              select
              label="Listing type"
              value={type}
              onChange={(e) => setType(e.target.value as MarketplaceListingType)}
            >
              <MenuItem value="AFH_SALE" disabled={!isBusinessAdmin}>
                AFH / Business For Sale
              </MenuItem>
              <MenuItem value="ELDER_GIFT">Gifts for Residents / Product</MenuItem>
            </TextField>

            {isAfhSale && (
              <Alert severity={missingBusinessForAfh ? "warning" : "info"}>
                {missingBusinessForAfh ? (
                  <>
                    No selected business. Choose your business first in{" "}
                    <RouterLink to="/biz/business">My Business</RouterLink>.
                  </>
                ) : !isBusinessAdmin ? (
                  <>Only business admins can post AFH sale listings for this business.</>
                ) : (
                  <>Listing will be linked to: {businessName}</>
                )}
              </Alert>
            )}

            <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={3}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Price (USD)"
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                fullWidth
              />
              <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
              <TextField label="State" value={state} onChange={(e) => setState(e.target.value)} fullWidth />
            </Stack>

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Contact
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                label="Contact email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                fullWidth
              />
              <TextField
                label="Contact phone (optional)"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                fullWidth
              />
            </Stack>

            <Divider />
            {isAfhSale ? (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Beds"
                  type="number"
                  value={beds}
                  onChange={(e) => setBeds(e.target.value)}
                  fullWidth
                />
                <TextField
                  select
                  label="Capacity (licensed)"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  fullWidth
                  helperText="Choose licensed resident capacity"
                >
                  {capacityOptions.map((v) => (
                    <MenuItem key={`cap-${v}`} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Current occupancy"
                  value={occupancy}
                  onChange={(e) => setOccupancy(e.target.value)}
                  fullWidth
                >
                  {occupancyOptions.map((v) => (
                    <MenuItem key={`occ-${v}`} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Product details
                </Typography>
                <TextField
                  select
                  label="Gift category"
                  value={giftCategory}
                  onChange={(e) => setGiftCategory(e.target.value)}
                  fullWidth
                >
                  {GIFT_CATEGORY_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                {isGiftCategoryOther && (
                  <TextField
                    label="Custom category"
                    value={giftCategoryOther}
                    onChange={(e) => setGiftCategoryOther(e.target.value)}
                    fullWidth
                  />
                )}
                <TextField
                  select
                  label="Age group"
                  value={ageGroup}
                  onChange={(e) => setAgeGroup(e.target.value)}
                  helperText="Optional"
                  fullWidth
                >
                  <MenuItem value="">Not specified</MenuItem>
                  {AGE_GROUP_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Product images
                  </Typography>
                  <input
                    ref={imageInputRef}
                    id="gift-image-upload-input"
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onPickGiftImage}
                  />
                  <label htmlFor="gift-image-upload-input">
                    <Button
                      size="small"
                      variant="outlined"
                      component="span"
                      startIcon={<UploadFileRoundedIcon />}
                      disabled={uploadingImage}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {uploadingImage ? "Uploading..." : "Add image"}
                    </Button>
                  </label>
                  {giftImageUrls.map((url, idx) => (
                    <Stack key={`gift-image-${idx}`} direction="row" spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={url}
                        alt={`Gift ${idx + 1}`}
                        sx={{ width: 60, height: 60, objectFit: "cover", borderRadius: 1 }}
                      />
                      <Typography variant="body2" sx={{ flexGrow: 1 }} noWrap>
                        {fileNameFromUrl(url) || `Image ${idx + 1}`}
                      </Typography>
                      <IconButton
                        aria-label="remove image"
                        onClick={() => removeGiftImageUrl(idx)}
                      >
                        <DeleteOutlineRoundedIcon />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            )}

            <Stack direction="row" justifyContent="flex-end">
              <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={save} disabled={saving}>
                {saving
                  ? "Saving..."
                  : !hasStandardPlan
                    ? `Publish Listing - $${listingPostFeeUsd}`
                    : "Publish Listing"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <PayPalScriptProvider options={{ clientId: paypalClientId || "" }}>
        <PayInvoiceDialog
          open={payOpen}
          invoice={invoice}
          onPaid={async () => {
            try {
              await publishListing();
              setPayOpen(false);
              setInvoice(null);
            } catch (e: any) {
              setError(e?.message || "Failed to publish listing.");
            }
          }}
          onClose={() => {
            setPayOpen(false);
            setInvoice(null);
          }}
          onCancel={() => {
            setPayOpen(false);
            setInvoice(null);
          }}
        />
      </PayPalScriptProvider>
    </Box>
  );
}
